/**
 * Wizard entry origin, carried in the URL (`?origin=`).
 *
 * The block creation wizard serves three flows: creating a block ("agrupación",
 * steps 1-5), re-entering from the block detail to change/configure the payment
 * method, and re-entering from the block detail to reactivate a churned
 * subscription. Only the two re-entries append the flag — its absence means the
 * creation flow, so the creation entry points need no changes and a
 * missing/malformed value safely degrades to the creation copy. Every step
 * navigation preserves the flag so the confirmation step (step 5) can pick the
 * matching title copy and the fiscal-data step can pick the session mode.
 */
export const CREATE_ORIGIN = 'create';
export const UPDATE_PAYMENT_ORIGIN = 'update-payment';
export const OVERDUE_PAYMENT_ORIGIN = 'overdue-payment';
export const REACTIVATION_ORIGIN = 'reactivation';

/** Flow the wizard was entered through. A new entry point needs its own value here: an origin the
 * wizard does not know reads as a creation, and the funnel counts it as one. */
export type WizardOrigin =
  | typeof CREATE_ORIGIN
  | typeof UPDATE_PAYMENT_ORIGIN
  | typeof OVERDUE_PAYMENT_ORIGIN
  | typeof REACTIVATION_ORIGIN;

const RE_ENTRY_ORIGINS: WizardOrigin[] = [UPDATE_PAYMENT_ORIGIN, OVERDUE_PAYMENT_ORIGIN, REACTIVATION_ORIGIN];

/**
 * Reads the wizard entry origin from the current URL.
 *
 * @param search - `location.search` of the current wizard route
 * @returns The declared origin, or `create` when the flag is absent or unknown
 */
export const resolveWizardOrigin = (search: string): WizardOrigin => {
  const origin = new URLSearchParams(search).get('origin');
  const match = RE_ENTRY_ORIGINS.find((known) => known === origin);
  return match ?? CREATE_ORIGIN;
};

/**
 * Checks whether the current wizard URL was entered from the block detail to
 * reactivate a churned subscription.
 *
 * @param search - `location.search` of the current wizard route
 * @returns True when `?origin=reactivation` is present
 */
export const isReactivationOrigin = (search: string): boolean => resolveWizardOrigin(search) === REACTIVATION_ORIGIN;

/**
 * Appends the entry origin to a wizard path when the current URL carries one,
 * preserving it across step navigations.
 *
 * Every re-entry origin is preserved, not just one: an origin the wizard knows
 * but this function does not would be dropped on the first step navigation, and
 * every downstream step would read the flow as a block creation.
 *
 * @param path - Target wizard path (may already carry a query string)
 * @param search - `location.search` of the current wizard route
 * @returns The path with the origin preserved
 */
export const withWizardOrigin = (path: string, search: string): string => {
  const origin = resolveWizardOrigin(search);
  if (origin === CREATE_ORIGIN) return path;
  return `${path}${path.includes('?') ? '&' : '?'}origin=${origin}`;
};
